/**
 * "Billable?" toggle for a single care-time entry.
 *
 * Presentation-inverted view of the persisted `nonBillable` flag: the switch
 * reads "Billable?" and is ON by default (every entry is billable unless
 * explicitly excluded), so toggling it OFF marks the entry non-billable. The
 * caption sits to the left of the control and is dimmed. Shared by the
 * activity-log row and the billing modal's minute log so the control looks and
 * behaves identically in both surfaces.
 *
 * @agentic connections
 */

import { useState } from "react";
import { Switch } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { showErrorNotification } from "@benchmark/ui/utils";
import { useMedplum } from "@medplum/react";
import type { Observation } from "@medplum/fhirtypes";
import { isCareTimeNonBillable } from "@benchmark/core/constants";
import { careTimeToForm } from "@benchmark/core/adapters";
import { createCareTimeFacade } from "@/facades/care-time.facade";
import { notifyError } from "./MonthlyBillingCard";

/** Props for `BillableToggle`. @agentic connections */
export type BillableToggleProps = {
  observation: Observation;
  /**
   * Called with the persisted observation after a successful toggle so callers
   * holding a local copy (e.g. the billing modal's minute log) can update
   * optimistically. The activity log relies on event-driven refetch instead.
   */
  onToggled?: (updated: Observation) => void;
  /**
   * Render the "Billable?" caption beside the switch. Off in the billing
   * minute log, where a "Billable?" column header already labels the control.
   * Defaults to `true`.
   */
  showCaption?: boolean;
  /**
   * Accessible name for the switch. Defaults to "Toggle billable"; callers
   * rendering many toggles in a list (e.g. the billing minute log) should pass
   * a row-specific label so screen readers can tell them apart.
   */
  ariaLabel?: string;
};

/**
 * Toggle a care-time entry between billable and non-billable.
 * @agentic connections
 */
export const BillableToggle = ({
  observation,
  onToggled,
  showCaption = true,
  ariaLabel = "Toggle billable",
}: BillableToggleProps) => {
  const medplum = useMedplum();
  const [toggling, setToggling] = useState(false);
  const nonBillable = isCareTimeNonBillable(observation);
  const minutes = observation.valueQuantity?.value ?? 0;

  const handleChange = async (billable: boolean) => {
    const nextNonBillable = !billable;
    const form = careTimeToForm(observation);
    if (!form) {
      showErrorNotification(
        "This entry is missing data required to update it.",
        { title: "Could not update billing status" },
      );
      return;
    }
    setToggling(true);
    try {
      const facade = createCareTimeFacade(medplum);
      const { observation: updated } = await facade.upsert({
        formData: { ...form, nonBillable: nextNonBillable },
        existingObservation: observation,
      });
      notifications.show({
        color: nextNonBillable ? "gray" : "teal",
        title: nextNonBillable ? "Marked non-billable" : "Marked billable",
        message: `${minutes} min ${nextNonBillable ? "excluded from" : "included in"} CoCM billing.`,
      });
      onToggled?.(updated);
    } catch (err) {
      notifyError("Could not update billing status", err);
    } finally {
      setToggling(false);
    }
  };

  return (
    <Switch
      size="xs"
      checked={!nonBillable}
      disabled={toggling}
      onChange={(e) => {
        void handleChange(e.currentTarget.checked);
      }}
      labelPosition="left"
      label={showCaption ? "Billable?" : undefined}
      aria-label={ariaLabel}
      styles={{ label: { color: "var(--mantine-color-dimmed)" } }}
    />
  );
};
